import { Plus, Save } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { toast } from 'sonner';
import { useActionsProcedures, useDomainesProcedures } from '@/api/hooks';
import type { DomaineProcedure } from '@/api/types';
import { useSession } from '@/auth/session';
import { Chargement, ErreurBloc, PageHeader } from '@/components/common';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { OngletsAdministration } from './Onglets';

const message = (e: unknown) => (e instanceof Error ? e.message : 'Action impossible.');

function LigneDomaine({ d, admin }: { d: DomaineProcedure; admin: boolean }) {
  const actions = useActionsProcedures();
  const [libelle, setLibelle] = useState(d.libelle);
  const [ordre, setOrdre] = useState(String(d.ordre));
  const [actif, setActif] = useState(d.actif);
  useEffect(() => {
    setLibelle(d.libelle);
    setOrdre(String(d.ordre));
    setActif(d.actif);
  }, [d]);
  const modifie = libelle !== d.libelle || Number(ordre) !== d.ordre || actif !== d.actif;
  const enregistrer = async () => {
    try {
      await actions.modifierDomaine.mutateAsync({ id: d.id, libelle: libelle.trim(), ordre: Number(ordre), actif });
      toast.success(`Domaine ${d.code} enregistré.`);
    } catch (e) {
      toast.error(message(e));
    }
  };
  return (
    <TableRow>
      <TableCell className="font-medium">{d.code}</TableCell>
      <TableCell>
        <Input value={libelle} onChange={(e) => setLibelle(e.target.value)} disabled={!admin} />
      </TableCell>
      <TableCell className="w-24">
        <Input type="number" min={0} value={ordre} onChange={(e) => setOrdre(e.target.value)} disabled={!admin} />
      </TableCell>
      <TableCell>
        <Checkbox checked={actif} onCheckedChange={(v) => setActif(v === true)} disabled={!admin} aria-label="Actif" />
      </TableCell>
      <TableCell className="text-right">
        {admin && (
          <Button type="button" size="sm" variant={modifie ? 'default' : 'outline'} disabled={!modifie || actions.modifierDomaine.isPending} onClick={enregistrer}>
            <Save className="size-4" /> Enregistrer
          </Button>
        )}
      </TableCell>
    </TableRow>
  );
}

/** Administration de la bibliothèque (§ 6.8) : domaines ; les paramètres du groupe PROCEDURES se règlent dans l'onglet Paramètres. */
export function DomainesProcedures() {
  const u = useSession()!.utilisateur;
  const admin = u.profils.some((p) => ['ADMINISTRATEUR', 'DIRECTEUR_RH', 'RESSOURCES_HUMAINES'].includes(p));
  const { data, isLoading, error } = useDomainesProcedures();
  const actions = useActionsProcedures();
  const [code, setCode] = useState('');
  const [libelle, setLibelle] = useState('');
  const creer = async () => {
    try {
      await actions.creerDomaine.mutateAsync({ code: code.trim().toUpperCase(), libelle: libelle.trim() });
      toast.success('Domaine créé.');
      setCode('');
      setLibelle('');
    } catch (e) {
      toast.error(message(e));
    }
  };
  return (
    <div>
      <PageHeader titre="Administration" sousTitre="Domaines de la bibliothèque des procédures : classement des fiches ; un domaine portant des procédures en cours ne se désactive pas." />
      <OngletsAdministration />
      <Card data-testid="domaines-procedures">
        <CardHeader>
          <CardTitle>Domaines des procédures</CardTitle>
          <CardDescription>
            Sept domaines initiaux (spécifications démarches RH v1.4, § 6.1). Délai d'affichage, échéance de consultation, rappels, visa du DG et suivi des directeurs :{' '}
            <Link to="/administration/parametres" className="text-primary hover:underline">
              groupe « Bibliothèque des procédures » des paramètres
            </Link>
            .
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {isLoading ? (
            <Chargement />
          ) : error || !data ? (
            <ErreurBloc erreur={error} />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Code</TableHead>
                  <TableHead>Libellé</TableHead>
                  <TableHead>Ordre</TableHead>
                  <TableHead>Actif</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.map((d) => (
                  <LigneDomaine key={d.id} d={d} admin={admin} />
                ))}
              </TableBody>
            </Table>
          )}
          {admin && (
            <div className="flex flex-wrap items-end gap-2 border-t pt-4">
              <div className="space-y-1">
                <label htmlFor="d-code" className="text-sm">
                  Code
                </label>
                <Input id="d-code" value={code} onChange={(e) => setCode(e.target.value)} placeholder="QUAL" className="w-32" />
              </div>
              <div className="min-w-64 flex-1 space-y-1">
                <label htmlFor="d-libelle" className="text-sm">
                  Libellé
                </label>
                <Input id="d-libelle" value={libelle} onChange={(e) => setLibelle(e.target.value)} placeholder="Qualité" />
              </div>
              <Button type="button" onClick={creer} disabled={!code.trim() || !libelle.trim() || actions.creerDomaine.isPending}>
                <Plus className="size-4" /> Ajouter un domaine
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
